import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiX, FiChevronLeft, FiChevronRight, FiImage, FiCheck, FiPackage } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Portfolio.css";

/* ════════════════════════════════════════════════════════════
   HERO BACKGROUND — replaced as requested
   ════════════════════════════════════════════════════════════ */
import heroBg from "../assets/uploads/services/1771523245971-481043020.jpg";

/* ════════════════════════════════════════════════════════════
   TAB PANEL IMAGE — single image replacing the three tabs
   ════════════════════════════════════════════════════════════ */
import tabSingleImg from "../assets/uploads/about/1212191629.webp";


/* ════════════════════════════════════════════════════════════
   SHOP IMAGES (webpack require.context) — for Featured Products
   ════════════════════════════════════════════════════════════ */
const _shopCtx = require.context(
  "../assets/uploads/shop",
  false,
  /\.(png|jpe?g|gif|webp|svg)$/i
);
const SHOP_IMAGES = _shopCtx.keys().reduce((map, key) => {
  map[key.replace(/^\.\//, "")] = _shopCtx(key);
  return map;
}, {});
function shopImg(filename) {
  if (!filename) return null;
  const API_BASE = process.env.REACT_APP_API_URL || "";
return SHOP_IMAGES[filename] ?? `${API_BASE}/uploads/shop/${filename}`;
}

/* ════════════════════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════════════════════ */
function wordLimit(text, n) {
  if (!text) return "";
  const words = text.split(" ").filter(Boolean);
  return words.length <= n ? text : words.slice(0, n).join(" ") + "…";
}

function stripHtmlText(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

function fmtPrice(n) {
  return "R " + Number(n).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ════════════════════════════════════════════════════════════
   FALLBACK TABS for Featured Products
   ════════════════════════════════════════════════════════════ */
const FALLBACK_TABS = [
  { key: "all",          label: "All Parts"     },
  { key: "brakes",       label: "Brakes"        },
  { key: "engine",       label: "Engine"        },
  { key: "electrical",   label: "Electrical"    },
  { key: "suspension",   label: "Suspension"    },
  { key: "transmission", label: "Transmission"  },
  { key: "oils",         label: "Oils & Fluids" },
];

/* ════════════════════════════════════════════════════════════
   FEATURED PRODUCT CARD
   ════════════════════════════════════════════════════════════ */
function FeaturedProductCard({ product }) {
  const price    = Number(product.sale_price || product.price);
  const oldPrice = product.sale_price ? Number(product.price) : null;
  const imgSrc   = shopImg(product.image1);
  const desc     = wordLimit(stripHtmlText(product.description || ""), 18);

  return (
    <div className="fp-card">
      <div className="fp-card__img-wrap">
        {imgSrc
          ? <img src={imgSrc} alt={product.name} loading="lazy" draggable="false" />
          : <div className="fp-card__no-img"><FiPackage size={40} /></div>
        }
        {product.discount_percent > 0 && (
          <span className="fp-card__discount">-{Math.round(product.discount_percent)}%</span>
        )}
      </div>
      <div className="fp-card__body">
        <h4 className="fp-card__name">{product.name}</h4>
        <div className="fp-card__prices">
          <span className="fp-card__price">{fmtPrice(price)}</span>
          {oldPrice && <span className="fp-card__old">{fmtPrice(oldPrice)}</span>}
        </div>
        {desc && <p className="fp-card__desc">{desc}</p>}
        <Link to={`/shop/${product.slug}`} className="fp-card__btn">
          VIEW PRODUCT
        </Link>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ════════════════════════════════════════════════════════════ */
function Counter({ to, suffix = "", delay = 0 }) {
  const [ref, inView] = useInView();
  const el = useRef(null);
  useEffect(() => {
    if (!inView) return;
    let start = null;
    const run = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 1800, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      if (el.current) el.current.textContent = Math.floor(eased * to);
      if (p < 1) requestAnimationFrame(run);
    };
    setTimeout(() => requestAnimationFrame(run), delay);
  }, [inView, to, delay]);
  return (
    <span ref={ref}>
      <span ref={el}>0</span>{suffix}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════
   LIGHTBOX
   ════════════════════════════════════════════════════════════ */
function Lightbox({ project, onClose }) {
  const [idx, setIdx] = useState(0);
  const [switching, setSwitching] = useState(false);

  const navigate = useCallback((dir) => {
    const next = idx + dir;
    if (next < 0 || next >= project.images.length) return;
    setSwitching(true);
    setTimeout(() => { setIdx(next); setSwitching(false); }, 200);
  }, [idx, project.images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowLeft")  navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="pf-lightbox" role="dialog" aria-modal="true">
      <div className="pf-lightbox__backdrop" onClick={onClose} />
      <div className="pf-lightbox__panel">
        <div className="pf-lightbox__header">
          <div className="pf-lightbox__title-wrap">
            <span className="pf-lightbox__title">{project.title}</span>
            <span className="pf-lightbox__counter">{idx + 1} / {project.images.length}&nbsp;·&nbsp;{project.subtitle}</span>
          </div>
          <button className="pf-lightbox__close" onClick={onClose} aria-label="Close"><FiX size={18} /></button>
        </div>
        <div className="pf-lightbox__stage">
          <img
            key={idx}
            src={project.images[idx]}
            alt={`${project.title} — image ${idx + 1}`}
            className={`pf-lightbox__img${switching ? " switching" : ""}`}
            draggable="false"
            onDragStart={(e) => e.preventDefault()}
          />
          {project.images.length > 1 && (
            <>
              <button className="pf-lightbox__nav pf-lightbox__nav--prev" onClick={() => navigate(-1)} disabled={idx === 0} aria-label="Previous image"><FiChevronLeft size={22} /></button>
              <button className="pf-lightbox__nav pf-lightbox__nav--next" onClick={() => navigate(1)} disabled={idx === project.images.length - 1} aria-label="Next image"><FiChevronRight size={22} /></button>
            </>
          )}
        </div>
        {project.images.length > 1 && (
          <div className="pf-lightbox__thumbs">
            {project.images.map((src, i) => (
              <div
                key={i}
                className={`pf-lightbox__thumb${i === idx ? " active" : ""}`}
                onClick={() => { setSwitching(true); setTimeout(() => { setIdx(i); setSwitching(false); }, 200); }}
              >
                <img src={src} alt={`Thumbnail ${i + 1}`} draggable="false" onDragStart={(e) => e.preventDefault()} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PROJECT CARD
   ════════════════════════════════════════════════════════════ */
function ProjectCard({ project, delay, onOpen }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`pf-card fade-in${inView ? " visible" : ""}`}
      style={{ transitionDelay: `${delay}s` }}
      onClick={() => onOpen(project)}
      onContextMenu={(e) => e.preventDefault()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(project)}
      aria-label={`View gallery for ${project.title}`}
    >
      <div className="pf-card__photo">
        <img src={project.images[0]} alt={project.title} loading="lazy" draggable="false" onDragStart={(e) => e.preventDefault()} />
        <div className="pf-card__overlay" />
        <div className="pf-card__count-badge"><FiImage size={10} />&nbsp;{project.images.length} PHOTOS</div>
        <div className="pf-card__view-btn" aria-hidden="true">
          <div className="pf-card__view-btn-inner"><FiImage size={13} />&nbsp; VIEW GALLERY</div>
        </div>
        <div className="pf-card__cat-bar">
          <span className="pf-card__cat">{project.category}</span>
        </div>
      </div>
      <div className="pf-card__body">
        <h3 className="pf-card__title">{project.title}</h3>
        <p className="pf-card__sub">{project.subtitle}</p>
        <div className="pf-card__link" aria-hidden="true">
          View Gallery
          <span className="pf-card__arrow"><FiArrowRight size={13} /></span>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════════ */
export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [lightboxProject, setLightboxProject] = useState(null);

  /* ── Services from API (replaces static portfolio images) ── */
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  /* ── Featured Products (All Parts section) ── */
  const [shopProducts, setShopProducts] = useState([]);
  const [shopProductsLoading, setShopProductsLoading] = useState(true);
  const [shopCategories, setShopCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const fpTrackRef = useRef(null);

  const [heroRef, heroInView] = useInView();
  const [gridRef, gridInView] = useInView();
  const [fpRef,   fpInView]   = useInView();
  const [processRef, processInView] = useInView();

  /* ── Fetch services ── */
  useEffect(() => {
    setServicesLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/services`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setServices(data);
        setServicesLoading(false);
      })
      .catch(() => setServicesLoading(false));
  }, []);

  /* ── Fetch shop products ── */
  useEffect(() => {
    setShopProductsLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/shop/products?limit=50&sort=newest`)
      .then(r => r.json())
      .then(data => {
        const prods = data.products || (Array.isArray(data) ? data : []);
        setShopProducts(prods);
        setShopProductsLoading(false);
      })
      .catch(() => setShopProductsLoading(false));
  }, []);

  /* ── Fetch shop categories ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/shop/categories`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setShopCategories(data); })
      .catch(() => {});
  }, []);

  /* ── Build projects from services API ── */
  // Each service becomes a project; images from image1..image5, title from service.title
  const buildProjectsFromServices = (svcs) => {
    return svcs
      .map((svc) => {
        const imgs = [svc.image1, svc.image2, svc.image3, svc.image4, svc.image5]
          .filter(Boolean)
          .map(f => `${process.env.REACT_APP_API_URL}/uploads/services/${f}`);
        if (imgs.length === 0) return null;
        return {
          id: svc.id,
          title: svc.title,
          subtitle: svc.slug ? svc.slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "",
          category: "Service",
          images: imgs,
        };
      })
      .filter(Boolean);
  };

  const projects = buildProjectsFromServices(services);
  console.log('PROJECTS:', JSON.stringify(projects, null, 2));

  /* ── Derive unique categories from services ── */
  const ALL = "All";
  const categories = [ALL, ...Array.from(new Set(projects.map(() => "Service")))];
  const filtered = activeFilter === ALL ? projects : projects.filter((p) => p.category === activeFilter);

  /* ── Context menu block ── */
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  /* ── Build tab list from shop categories ── */
  const dynamicTabs = [
    { key: "all", label: "All Parts" },
    ...shopCategories.slice(0, 6).map(c => ({ key: c.slug, label: c.name, catId: c.id })),
  ];
  const finalTabs = dynamicTabs.length >= 7
    ? dynamicTabs.slice(0, 7)
    : [...dynamicTabs, ...FALLBACK_TABS.slice(dynamicTabs.length)].slice(0, 7);

  /* ── Filter shop products by active tab ── */
  const filteredShopProducts = activeTab === "all"
    ? shopProducts
    : shopProducts.filter(p => {
        const tab = finalTabs.find(t => t.key === activeTab);
        if (tab?.catId) return p.category_id === tab.catId;
        const name = (p.name || "").toLowerCase();
        const cat  = (p.category_name || "").toLowerCase();
        return name.includes(activeTab) || cat.includes(activeTab);
      });

  const scrollFpLeft  = () => fpTrackRef.current?.scrollBy({ left: -320, behavior: "smooth" });
  const scrollFpRight = () => fpTrackRef.current?.scrollBy({ left:  320, behavior: "smooth" });

  /* Process steps data */
  const steps = [
    { n: "01", title: "Initial Consultation",        desc: "We start by understanding your vision, goals, and requirements — laying the groundwork for a successful project." },
    { n: "02", title: "Strategic Planning & Design", desc: "Our team creates detailed plans, timelines, and cost estimates — ensuring alignment before a single brick is laid." },
    { n: "03", title: "Construction & Execution",    desc: "With skilled teams and strict supervision, we bring the design to life on time, on budget, to the highest standards." },
    { n: "04", title: "Final Handover & Support",    desc: "We complete inspections, walk you through results, and remain available for any post-build support you need." },
  ];

  return (
    <main className="pf">

      {/* ══════ 1. HERO ══════ */}
      <section className="pf-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="pf-hero__overlay" />
        <div className="container pf-hero__body" ref={heroRef}>
          <div className={`pf-hero__label fade-in${heroInView ? " visible" : ""}`}>OUR PORTFOLIO</div>
          <h1
            className={`pf-hero__title fade-in${heroInView ? " visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            DRIVE WITH PRECISION
          </h1>
        </div>
        <div className="pf-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Portfolio</span>
        </div>

        {/* ── Single Tab Box ── */}
        <div className="pf-info-box__anchor">
          <div className="container pf-info-box__wrapper">
            <div className={`pf-info-box pf-info-box--single fade-in${heroInView ? " visible" : ""}`} style={{ transitionDelay: "0.2s" }}>
              {/* Single tab header */}
              <div className="pf-info-box__tabs pf-info-box__tabs--single">
                <div className="pf-info-box__tab pf-info-box__tab--single active">
                  EXTREME DIAGNOSTICS AND CAR REPAIRS
                </div>
              </div>
              {/* Single full-width image body */}
              <div className="pf-info-box__body pf-info-box__body--single">
                <div className="pf-info-box__img-wrap pf-info-box__img-wrap--full">
                  <img
                    src={tabSingleImg}
                    alt="Extreme Diagnostics and Car Repairs"
                    loading="lazy"
                    draggable="false"
                    onDragStart={e => e.preventDefault()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ ALL PARTS — FEATURED PRODUCTS (before portfolio grid) ══════ */}
      <section className="fp-section fp-section--portfolio" ref={fpRef}>

        {/* Tab bar */}
        <div className="fp-section__tabs-wrap">
          <div className="container">
            <div className="fp-section__tabs">
              {finalTabs.map(tab => (
                <button
                  key={tab.key}
                  className={`fp-section__tab ${activeTab === tab.key ? "fp-section__tab--active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cards track */}
        <div className="fp-section__track-wrap">
          <div className="container fp-section__track-container">

            <button className="fp-section__arrow fp-section__arrow--left" onClick={scrollFpLeft} aria-label="Scroll left">
              <FiChevronLeft size={20} />
            </button>

            {shopProductsLoading ? (
              <div className="fp-section__track" ref={fpTrackRef}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="fp-card fp-card--skeleton" />
                ))}
              </div>
            ) : filteredShopProducts.length === 0 ? (
              <div className="fp-section__empty">
                <FiPackage size={40} />
                <p>No products in this category yet.</p>
                <Link to="/shop" className="fp-section__empty-link">Browse all products →</Link>
              </div>
            ) : (
              <div className={`fp-section__track fade-in ${fpInView ? "visible" : ""}`} ref={fpTrackRef}>
                {filteredShopProducts.map(product => (
                  <FeaturedProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            <button className="fp-section__arrow fp-section__arrow--right" onClick={scrollFpRight} aria-label="Scroll right">
              <FiChevronRight size={20} />
            </button>

          </div>
        </div>

        {/* View all */}
        <div className="fp-section__footer">
          <Link to="/shop" className="fp-section__view-all">
            View All Products <FiArrowRight size={14} />
          </Link>
        </div>

      </section>

      {/* ══════ 2 + 3. PORTFOLIO GRID — fetched from services API ══════ */}
      <section className="pf-grid pf-grid--tab-offset" ref={gridRef}>
  <div className="container">
    <div className={`pf-grid__head fade-in${gridInView ? " visible" : ""}`}>
      <div className="pf-grid__tag">OUR PORTFOLIO</div>
      <h2 className="pf-grid__h2">VISIT OUR WORKSHOP</h2>
      <p className="pf-grid__sub">
        And Get the Best Car Care
      </p>
    </div>

    {/* ── YouTube Videos Row ── */}
    <div className="pf-videos">
      {[
        { url: "https://www.youtube.com/embed/PkNpO0fC6do", title: "Workshop Video 1" },
        { url: "https://www.youtube.com/embed/0kZ3TZvYg10", title: "Workshop Short 1" },
        { url: "https://www.youtube.com/embed/FOz1xrIPZS0", title: "Workshop Short 2" },
      ].map((vid, i) => (
        <div key={i} className="pf-videos__item">
          <iframe
            src={vid.url}
            title={vid.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ))}
    </div>

    {servicesLoading && (
      <div className="pf-grid__skeleton-row">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="pf-card pf-card--skeleton-item" />
        ))}
      </div>
    )}

    {!servicesLoading && projects.length === 0 && (
      <p className="pf-grid__empty">No service gallery images found. Please add images to your services.</p>
    )}

    {!servicesLoading && projects.length > 0 && (
      <div className="pf-projects">
        {filtered.map((project, i) => (
          <ProjectCard
            key={project.id}
            project={project}
            delay={Math.min(i * 0.07, 0.35)}
            onOpen={setLightboxProject}
          />
        ))}
      </div>
    )}
  </div>
</section>

    {lightboxProject && (
        <Lightbox
          project={lightboxProject}
          onClose={() => setLightboxProject(null)}
        />
      )}
    </main>
  );
}